import dynamic from 'next/dynamic';
import PropTypes from 'prop-types';
import { forwardRef, useEffect } from 'react';
import { twMerge } from 'tailwind-merge';

import Box from 'shopper/components/Box';
import Heading from 'shopper/components/Heading';
import Link from 'shopper/components/Link';
import Text from 'shopper/components/Text';

import useDialog from 'hooks/useDialog';

import { sendEvent } from 'lib/gtag';
import placeholder from 'lib/placeholder';

import WishlistSuggestionsKeywords from './WishlistSuggestionsKeywords';

const WishlistDialog = dynamic(() => import('./WishlistDialog'), {
  ssr: false,
});

const WishlistSuggestions = forwardRef(
  (
    {
      className,
      gaEventCategory,
      headingProps = {
        size: 'size5',
        as: 'span',
      },
      headingPropsClassName = '',
      keywordButtonType = null,
      keywordButtonTypeCanChange = false,
      keywordsDisplay = [],
      withEditWishlist = false,
      ...rest
    },
    ref
  ) => {
    const { showDialog } = useDialog();

    useEffect(() => {
      sendEvent({ gaEventCategory, action: 'appear' });
    }, []);

    const onEditWishlistClick = () => {
      showDialog(WishlistDialog);
    };

    return (
      <Box
        ref={ref}
        className={twMerge(
          'w-full flex-col flex-wrap overflow-hidden px-4 py-4 md:px-6',
          className
        )}
        {...rest}
      >
        <Heading
          className={twMerge('mb-2 md:mb-4', headingPropsClassName)}
          {...headingProps}
        >
          {placeholder('titles.wishlist')}
        </Heading>
        <Text className="mb-6 max-w-full md:mb-4">
          {placeholder('descriptions.howWishlistWorks')}
        </Text>
        {withEditWishlist && (
          <Link className="mb-4 hidden lg:block" onClick={onEditWishlistClick}>
            {placeholder('actions.editWishlistShort')}
          </Link>
        )}
        <WishlistSuggestionsKeywords
          gaEventCategory={gaEventCategory}
          keywordButtonType={keywordButtonType}
          keywordButtonTypeCanChange={keywordButtonTypeCanChange}
          keywordsDisplay={keywordsDisplay}
        />
      </Box>
    );
  }
);

WishlistSuggestions.displayName = 'WishlistSuggestions';

WishlistSuggestions.propTypes = {
  gaEventCategory: PropTypes.string.isRequired,
  headingProps: PropTypes.shape(),
  keywordButtonType: PropTypes.string,
  keywordButtonTypeCanChange: PropTypes.bool,
  keywordsDisplay: PropTypes.arrayOf(PropTypes.string),
  withEditWishlist: PropTypes.bool,
};

export default WishlistSuggestions;
